/**
 * Constructor for Countdown utilities
 * @param {object} setup
 * @constructor
 */
function CountdownUtils(setup) {
  this.countdownSetup = setup;

  // Reference to setInterval timer
  this.intervalId = 0;

  // Initial state
  this.state = {
    days: 0,
    daysLabel: '',
    daysShow: false,

    hours: 0,
    hoursLabel: '',
    hoursShow: false,

    minutes: 0,
    minutesLabel: '',
    minutesShow: false,

    seconds: 0,
    secondsLabel: '',
    secondsShow: false,

    messageFirstPart: '',
    messageSecondPart: '',

    color: 'green',

    display: false
  };
}

CountdownUtils.prototype.consts = {
  DAY: 'day',
  HOUR: 'hr',
  MINUTE: 'min',
  SECOND: 'sec',
  PLURAL: 's',

  PLACEHOLDER: '[%TIME%]',

  INTERVAL: 1000,

  SECONDS_IN_DAY: 86400,
  SECONDS_IN_HOUR: 3600,
  SECONDS_IN_MINUTE: 60
};

/**
 * Initialize the countdown
 * @param {object} setup
 */
CountdownUtils.prototype.start = function start() {
  const messageSplit = this.countdownSetup.message.split(this.consts.PLACEHOLDER);
  Object.assign(this.state, {
    messageSplit,
    messageFirstPart: messageSplit.length > 0 ? messageSplit[0] : '',
    messageSecondPart: messageSplit.length > 1 ? messageSplit[1] : ''
  });

  this.startCountdown(this.countdownSetup.currentTimeInSeconds);
};

/**
 * Starts the countdown interval
 * @param {number} initialTimeInSeconds
 */
CountdownUtils.prototype.startCountdown = function startCountdown(initialTimeInSeconds) {
  const initialRemainingSeconds = this.countdownSetup.endTimeInSeconds - initialTimeInSeconds;
  const initialLocalTimeInSeconds = parseInt(new Date().getTime() / 1000, 10);
  if (initialRemainingSeconds > 0) {
    this.intervalId = setInterval(() => {
      const currentLocalTimeInSeconds = parseInt(new Date().getTime() / 1000, 10);
      const elapsedSeconds = currentLocalTimeInSeconds - initialLocalTimeInSeconds;
      let remainingSeconds = initialRemainingSeconds - elapsedSeconds;
      if (remainingSeconds < 0) {
        remainingSeconds = 0;
      }
      this.updateCountdown(remainingSeconds);
    }, this.consts.INTERVAL);
  } else {
    this.updateCountdown(0);
  }
  
  const currentLocalTimeInSeconds = parseInt(new Date().getTime() / 1000, 10);
  const elapsedSeconds = currentLocalTimeInSeconds - initialLocalTimeInSeconds;
  const remainingSeconds = initialRemainingSeconds - elapsedSeconds;
  this.updateCountdown(remainingSeconds > 0 ? remainingSeconds : 0);
  
  if (this.countdownSetup.deliveryType === 'next-day' &&
    this.countdownSetup.countdownType === 'delivery') {
    window.appState.dispatch({
      type: 'NEXT_DAY_DELIVERY_COUNTDOWN',
      payload: {
        remainingSeconds: remainingSeconds,
        clockColor: this.getColor(remainingSeconds),
        enabled: this.state.display
      }
    });
  }
    
};

/**
 * Updates the countdown with remaining seconds
 * @param {number} remainingSeconds
 */
CountdownUtils.prototype.updateCountdown = function updateCountdown(remainingSeconds) {
  const currentTimeInSeconds = this.countdownSetup.endTimeInSeconds - remainingSeconds;
  const messageSplit = this.state.messageSplit;

  if (remainingSeconds > 0) {
    const timeUnits = this.convertSecondToDHMS(remainingSeconds);

    // Don't display countdown yet
    if (currentTimeInSeconds < this.countdownSetup.messageStartTimeInSeconds) {
      this.state.display = false;

    // Static message is shown before timer starts
    } else if (currentTimeInSeconds < this.countdownSetup.messageEndTimeInSeconds) {
      this.state.messageFirstPart = this.countdownSetup.messageBefore;
      this.state.display = true;

    // Just before the timer starts (usually 15 minutes)
    } else if (currentTimeInSeconds < this.countdownSetup.startTimeInSeconds &&
      this.countdownSetup.messageStartTimeInSeconds > 0) {
      this.state.display = false;

    // Countdown timer is shown
    } else if (currentTimeInSeconds > this.countdownSetup.startTimeInSeconds &&
      currentTimeInSeconds < this.countdownSetup.endTimeInSeconds) {
      Object.assign(this.state, {
        days: timeUnits.days,
        daysLabel: this.consts.DAY +
        (timeUnits.days === 0 || timeUnits.days > 1 ? this.consts.PLURAL : ''),
        daysShow: timeUnits.days > 0,

        hours: timeUnits.hours,
        hoursLabel: this.consts.HOUR +
        (timeUnits.hours === 0 || timeUnits.hours > 1 ? this.consts.PLURAL : ''),
        hoursShow: timeUnits.days !== 0 || timeUnits.hours !== 0,

        minutes: timeUnits.minutes,
        minutesLabel: this.consts.MINUTE +
        (timeUnits.minutes === 0 || timeUnits.minutes > 1 ? this.consts.PLURAL : ''),
        minutesShow: timeUnits.days !== 0 || timeUnits.hours !== 0 || timeUnits.minutes !== 0,

        seconds: timeUnits.seconds,
        secondsLabel: this.consts.SECOND +
        (timeUnits.seconds === 0 || timeUnits.seconds > 1 ? this.consts.PLURAL : ''),
        secondsShow: true,

        messageFirstPart: messageSplit.length > 0 ? messageSplit[0] : '',
        messageSecondPart: messageSplit.length > 1 ? messageSplit[1] : '',

        color: this.getColor(remainingSeconds),
        display: true
      });

    } else {
      this.state.display = false;
    }

  } else {
    const newState = {
      daysShow: false,
      hoursShow: false,
      minutesShow: false,
      secondsShow: false,
      display: this.countdownSetup.messageAfter !== ''
    };

    // Static message is shown after countdown ended
    if (this.countdownSetup.messageAfter !== '') {
      newState.messageFirstPart = this.countdownSetup.messageAfter;
    }

    clearInterval(this.intervalId);
    Object.assign(this.state, newState);
  }
  this.countdownSetup.onChange(this.state);
  return this.state;
};

/**
 * Returns color of countdown
 * @param {number} remainingSeconds
 */
CountdownUtils.prototype.getColor = function getColor(remainingSeconds) {
  let color = 'green';
  if (remainingSeconds < this.countdownSetup.redCountdown) {
    color = 'red';
  } else if (remainingSeconds < this.countdownSetup.amberCountdown) {
    color = 'orange';
  }
  return color;
};

/**
 * Converts date in seconds to days, hours, minutes and seconds
 * @param {number} seconds
 * @returns {{days: number, hours: number, minutes: number, seconds: number}}
 */
CountdownUtils.prototype.convertSecondToDHMS = function convertSecondToDHMS(seconds) {
  let days = 0;
  let hours = 0;
  let minutes = 0;

  if (seconds > 0) {
    days = Math.floor(seconds / this.consts.SECONDS_IN_DAY);
    seconds -= days * this.consts.SECONDS_IN_DAY;

    hours = Math.floor(seconds / this.consts.SECONDS_IN_HOUR);
    seconds -= hours * this.consts.SECONDS_IN_HOUR;

    minutes = Math.floor(seconds / this.consts.SECONDS_IN_MINUTE);
    seconds -= minutes * this.consts.SECONDS_IN_MINUTE;
  } else {
    seconds = 0;
  }

  return {
    days, hours, minutes, seconds
  };
};

module.exports = CountdownUtils;
